import React, { useState } from 'react';

/**
 * The page shell the guest-facing booking pages share (#791 E9): the funnel's
 * reschedule page (`PublicReschedule`) and the guest manage page
 * (`PublicBookingManage`).
 *
 * Both components are MODULE-LEVEL on purpose. The funnel page used to declare
 * its shell inside render, so every state change gave React a new component
 * type, remounted the whole subtree and dropped focus out of the reason
 * textarea after each keystroke. Never define either of these inside another
 * component.
 *
 * This module imports nothing from `react-router-dom` and draws no `grid`
 * element: `PublicReschedule.calendar-error.test.jsx` mocks the router with
 * `useParams` only and finds the slot buttons with `.grid button`.
 */

/**
 * The full-height `#FAFAF5` page with the centred `max-w-md` column and a 16px
 * side gutter. "Powered by NurtureCRM" renders only with `footer`, so a page
 * that never had one (PublicReschedule) looks the same as before.
 */
export function PublicCardShell({ children, footer = false }) {
    return (
        <main className="min-h-screen bg-[#FAFAF5] flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md">
                {children}
                {footer && (
                    <p className="mt-6 text-center text-xs text-stone-500">Powered by NurtureCRM</p>
                )}
            </div>
        </main>
    );
}

/**
 * The organisation's logo. Renders nothing without a `src`, and hides itself
 * for good if the image fails to load (the org-logo proxy answers 404 when no
 * logo is configured). It owns its load-error state, keyed by the `src` that
 * failed, so a new `src` is tried afresh. Each caller decides where it goes:
 * PublicReschedule draws it inside each card, the manage page above the cards.
 */
export function PublicLogo({ src }) {
    const [failedSrc, setFailedSrc] = useState(null);
    if (!src || failedSrc === src) return null;
    return (
        <img
            src={src}
            alt=""
            onError={() => setFailedSrc(src)}
            className="max-h-10 mx-auto mb-4 object-contain"
            data-testid="public-logo"
        />
    );
}
